import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Container,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Row,
  Col,
  Modal
} from 'react-bootstrap';
import {
  useJsApiLoader
} from '@react-google-maps/api';
import SimplePhoneInput from './SimplePhoneInput';
import DateTimePicker from './DateTimePicker';
import { generateDeviceFingerprint, getDevicePlatform } from '../utils/deviceFingerprint';

// Define libraries array as a constant outside the component
const GOOGLE_MAPS_LIBRARIES = ['places'];

/**
 * Clean Google Maps formatted address by removing unwanted information
 * Removes: "UK", double town names, "Managed by..." text
 */
function cleanGoogleMapsAddress(address) {
  if (!address || typeof address !== 'string') {
    return address;
  }
  
  let cleaned = address.trim();
  
  // Remove "Managed by..." text and everything after it
  const managedByIndex = cleaned.indexOf('Managed by');
  if (managedByIndex !== -1) {
    cleaned = cleaned.substring(0, managedByIndex).trim();
  }
  
  // Remove ", UK" or "UK" at the end (case insensitive)
  cleaned = cleaned.replace(/,\s*UK\s*$/i, '').trim();
  cleaned = cleaned.replace(/\s+UK\s*$/i, '').trim();
  
  // Remove duplicate town names
  // Split by comma and process each part
  const parts = cleaned.split(',').map(p => p.trim()).filter(p => p.length > 0);
  const uniqueParts = [];
  const seen = new Set();
  
  // Common street suffixes to ignore when comparing
  const streetSuffixes = ['road', 'street', 'avenue', 'lane', 'drive', 'way', 'close', 'place', 
    'court', 'gardens', 'park', 'hill', 'terrace', 'row', 'crescent', 'grove', 'walk', 
    'square', 'yard', 'mews', 'end', 'corner', 'view', 'rise', 'vale', 'green', 'mead', 
    'bank', 'bridge', 'gate', 'side', 'wood', 'heath', 'ford', 'well'];
  
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    // Normalize for comparison (lowercase, remove common street suffixes)
    const normalized = part.toLowerCase()
      .replace(new RegExp(`\\s+(${streetSuffixes.join('|')})$`, 'i'), '')
      .trim();
    
    // Skip if we've seen this normalized part before (avoid duplicates)
    // But always keep the last part (usually postcode or main area) and postcodes (contain numbers/letters pattern)
    const isPostcode = /^[A-Z]{1,2}\d{1,2}\s?\d[A-Z]{2}$/i.test(part);
    if (!seen.has(normalized) || i === parts.length - 1 || isPostcode) {
      uniqueParts.push(part);
      if (!isPostcode) {
        seen.add(normalized);
      }
    }
  }
  
  cleaned = uniqueParts.join(', ').trim();
  
  // Final cleanup: remove trailing commas
  cleaned = cleaned.replace(/,\s*$/, '').trim();
  
  return cleaned;
}

// Banner display component (matches LeadForm implementation)
const BannerDisplay = ({ banners, currentIndex, onBannerClick }) => {
  // Show placeholder when no banners are available
  if (!banners || banners.length === 0) {
    return (
      <div className="mb-3">
        <div className="bg-light border rounded p-3 text-center">
          <div className="text-muted">Your local ad here</div>
        </div>
      </div>
    );
  }

  const currentBanner = banners[currentIndex];
  if (!currentBanner) return null;

  return (
    <div className="mb-3">
      <div 
        key={currentBanner._id} 
        className="alert alert-info py-2 px-3 mb-2"
        style={{ cursor: 'pointer' }}
        onClick={() => onBannerClick && onBannerClick(currentBanner._id)}
        title="Click to track engagement"
      >
        {currentBanner.imageDataUrl ? (
          <>
            <img
              src={currentBanner.imageDataUrl}
              alt="banner"
              className="mb-2"
              style={{ width: '100%', height: 'auto', display: 'block', borderRadius: 4 }}
            />
            <div>
              <strong>{currentBanner.title}</strong>
              <div className="small mb-0">{currentBanner.message}</div>
            </div>
          </>
        ) : (
          <div className="d-flex align-items-center gap-2">
            <div>
              <strong>{currentBanner.title}</strong>
              <div className="small mb-0">{currentBanner.message}</div>
            </div>
          </div>
        )}
        {/* Banner rotation indicator */}
        {banners.length > 1 && (
          <div className="d-flex justify-content-center mt-2">
            {banners.map((_, index) => (
              <div
                key={index}
                className={`mx-1 rounded-circle ${index === currentIndex ? 'bg-primary' : 'bg-secondary'}`}
                style={{ width: '8px', height: '8px' }}
              />
            ))}
          </div>
        )}
        {/* Banner info */}
        <div className="text-center mt-2">
          <small className="text-muted">
            Banner {currentIndex + 1} of {banners.length}
            {currentBanner.targetPostcodes && currentBanner.targetPostcodes.length > 0 && (
              <span> • Targeted to: {currentBanner.targetPostcodes.join(', ')}</span>
            )}
          </small>
        </div>
      </div>
    </div>
  );
};

// Mobile fallback component without Google Maps autocomplete
const MobileBookingForm = ({
  formData, handleChange, handleDateChange, fieldErrors,
  bookingLink, handleSubmit, submitting, setError, showPassengerWarning
}) => {
  return (
    <>

      <Form onSubmit={handleSubmit}>
        {/* Date & Time - Using DateTimePicker with ASAP toggle */}
        <Form.Group className="mb-3">
        <DateTimePicker
          value={formData.dateTime}
          onChange={handleDateChange}
          label="Date & Time"
          className="mb-3"
          use24Hour={false}
          show24HourToggle={false}
        />
        {fieldErrors.dateTime && (
          <div className="invalid-feedback d-block">
            {fieldErrors.dateTime}
          </div>
        )}
      </Form.Group>

      {/* Name */}
      <Form.Group className="mb-3">
        <Form.Label>Full Name *</Form.Label>
        <Form.Control
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          placeholder="Enter your full name"
          isInvalid={!!fieldErrors.name}
        />
        {fieldErrors.name && (
          <Form.Control.Feedback type="invalid">
            {fieldErrors.name}
          </Form.Control.Feedback>
        )}
      </Form.Group>

      {/* Email */}
      <Form.Group className="mb-3">
        <Form.Label>Email Address *</Form.Label>
        <Form.Control
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          required
          placeholder="Enter your email address"
          isInvalid={!!fieldErrors.email}
        />
        {fieldErrors.email && (
          <Form.Control.Feedback type="invalid">
            {fieldErrors.email}
          </Form.Control.Feedback>
        )}
      </Form.Group>

      {/* Phone */}
      <Form.Group className="mb-3">
        <Form.Label>Phone Number *</Form.Label>
        <SimplePhoneInput
          value={formData.phone}
          onChange={(value) => {
            handleChange({ target: { name: 'phone', value } });
            // Clear field error when user starts typing
            if (fieldErrors.phone) {
              setError('');
            }
          }}
          required
          className={fieldErrors.phone ? 'is-invalid' : ''}
        />
        {fieldErrors.phone && (
          <div className="invalid-feedback">
            {fieldErrors.phone}
          </div>
        )}
      </Form.Group>

      {/* From */}
      <Form.Group className="mb-3">
        <Form.Label>From Address *</Form.Label>
        <Form.Control
          type="text"
          name="from"
          value={formData.from}
          onChange={handleChange}
          required
          placeholder="Pickup address"
          isInvalid={!!fieldErrors.from}
        />
        {fieldErrors.from && (
          <Form.Control.Feedback type="invalid">
            {fieldErrors.from}
          </Form.Control.Feedback>
        )}
      </Form.Group>

      {/* To */}
      <Form.Group className="mb-3">
        <Form.Label>To Address *</Form.Label>
        <Form.Control
          type="text"
          name="to"
          value={formData.to}
          onChange={handleChange}
          required
          placeholder="Destination address"
          isInvalid={!!fieldErrors.to}
        />
        {fieldErrors.to && (
          <Form.Control.Feedback type="invalid">
            {fieldErrors.to}
          </Form.Control.Feedback>
        )}
      </Form.Group>

      {/* Passengers */}
      <Form.Group className="mb-3">
        <Form.Label>Number of Passengers *</Form.Label>
        <Form.Select
          name="passengers"
          value={formData.passengers}
          onChange={handleChange}
          required
          isInvalid={!!fieldErrors.passengers}
        >
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
            <option key={num} value={num}>
              {num} {num === 1 ? 'passenger' : 'passengers'}
            </option>
          ))}
        </Form.Select>
        {fieldErrors.passengers && (
          <Form.Control.Feedback type="invalid">
            {fieldErrors.passengers}
          </Form.Control.Feedback>
        )}
        {showPassengerWarning && bookingLink?.driverCarSeats && (
          <Alert variant="warning" className="mt-2 small">
            <strong>⚠️ Capacity Notice:</strong> This driver can carry up to {bookingLink.driverCarSeats} passengers. 
            If you need more, the driver will arrange multiple trips or involve other local drivers. 
            Your driver will contact you to discuss.
          </Alert>
        )}
      </Form.Group>

      {/* Return Trip */}
      <Form.Group className="mb-3">
        <Form.Check
          type="checkbox"
          name="returnTrip"
          checked={formData.returnTrip}
          onChange={handleChange}
          label="Return trip required"
        />
      </Form.Group>

      {/* Special Requirements */}
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              name="dog"
              checked={formData.dog}
              onChange={handleChange}
              label="Dog/Pet"
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              name="disabled"
              checked={formData.disabled}
              onChange={handleChange}
              label="Disabled access required"
            />
          </Form.Group>
        </Col>
      </Row>

      {/* Notes */}
      <Form.Group className="mb-4">
        <Form.Label>Special Instructions</Form.Label>
        <Form.Control
          as="textarea"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows={3}
          placeholder="Any special requirements or instructions..."
        />
      </Form.Group>

      {/* Submit Button */}
      <div className="d-grid">
        <Button
          type="submit"
          variant="primary"
          size="lg"
          disabled={submitting}
        >
          {submitting ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Submitting...
            </>
          ) : (
            'Submit Booking Request'
          )}
        </Button>
      </div>
    </Form>
    </>
  );
};

export default function BookingLinkForm() {
  const { linkId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [bookingLink, setBookingLink] = useState(null);
  const [mapLoadTimeout, setMapLoadTimeout] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [banners, setBanners] = useState([]);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    from: '',
    to: '',
    dateTime: new Date(),
    isASAP: false,
    returnTrip: false,
    passengers: '1',
    dog: false,
    disabled: false,
    notes: ''
  });
  
  const [showPassengerWarning, setShowPassengerWarning] = useState(false);
  const [showPassengerModal, setShowPassengerModal] = useState(false);
  const [passengerCapacity, setPassengerCapacity] = useState(null);

  // Refs for Google Places Autocomplete
  const fromInputRef = useRef(null);
  const toInputRef = useRef(null);
  const fromAutocompleteRef = useRef(null);
  const toAutocompleteRef = useRef(null);

  // Detect mobile device with enhanced mobile Chrome detection
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES
  });

  // Initialize Google Places Autocomplete when Google Maps loads
  useEffect(() => {
    if (!isLoaded || loadError || !window.google || !window.google.maps || !window.google.maps.places) {
      return;
    }

    const initializeAutocomplete = () => {
      try {
        // Initialize From field autocomplete
        if (fromInputRef.current && !fromAutocompleteRef.current) {
          fromAutocompleteRef.current = new window.google.maps.places.Autocomplete(
            fromInputRef.current,
            {
              componentRestrictions: { country: 'uk' },
              fields: ['formatted_address', 'geometry', 'name'],
              types: ['address']
            }
          );

          fromAutocompleteRef.current.addListener('place_changed', () => {
            const place = fromAutocompleteRef.current.getPlace();
            if (place && place.formatted_address) {
              const cleanedAddress = cleanGoogleMapsAddress(place.formatted_address);
              setFormData(prev => ({ ...prev, from: cleanedAddress }));
              // Update the input field with cleaned address
              if (fromInputRef.current) {
                fromInputRef.current.value = cleanedAddress;
              }
              // Clear field error when user selects from autocomplete
              if (fieldErrors.from) {
                setFieldErrors(prev => ({ ...prev, from: '' }));
              }
            }
          });
        }

        // Initialize To field autocomplete
        if (toInputRef.current && !toAutocompleteRef.current) {
          toAutocompleteRef.current = new window.google.maps.places.Autocomplete(
            toInputRef.current,
            {
              componentRestrictions: { country: 'uk' },
              fields: ['formatted_address', 'geometry', 'name'],
              types: ['address']
            }
          );

          toAutocompleteRef.current.addListener('place_changed', () => {
            const place = toAutocompleteRef.current.getPlace();
            if (place && place.formatted_address) {
              const cleanedAddress = cleanGoogleMapsAddress(place.formatted_address);
              setFormData(prev => ({ ...prev, to: cleanedAddress }));
              // Update the input field with cleaned address
              if (toInputRef.current) {
                toInputRef.current.value = cleanedAddress;
              }
              // Clear field error when user selects from autocomplete
              if (fieldErrors.to) {
                setFieldErrors(prev => ({ ...prev, to: '' }));
              }
            }
          });
        }
      } catch (error) {
        console.error('Error initializing autocomplete:', error);
      }
    };

    // Small delay to ensure DOM is ready
    const timer = setTimeout(initializeAutocomplete, 100);
    return () => {
      clearTimeout(timer);
      // Cleanup autocomplete listeners
      if (fromAutocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners?.(fromAutocompleteRef.current);
        fromAutocompleteRef.current = null;
      }
      if (toAutocompleteRef.current) {
        window.google?.maps?.event?.clearInstanceListeners?.(toAutocompleteRef.current);
        toAutocompleteRef.current = null;
      }
    };
  }, [isLoaded, loadError, fieldErrors.from, fieldErrors.to]);


  // Set timeout for map loading on mobile
  useEffect(() => {
    if (isMobile) {
      const timeout = setTimeout(() => {
        if (!isLoaded && !loadError) {
          console.warn('Google Maps API loading timeout on mobile device');
          setMapLoadTimeout(true);
        }
      }, 10000); // 10 second timeout

      return () => clearTimeout(timeout);
    }
  }, [isMobile, isLoaded, loadError]);

  const loadBookingLink = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`/booking-links/public/${linkId}`);
      setBookingLink(data);
      
      // Pre-fill form data if available
      if (data.prefillData) {
        setFormData(prev => ({
          ...prev,
          from: data.prefillData.from || '',
          to: data.prefillData.to || '',
          notes: data.prefillData.notes || ''
        }));
      }
    } catch (err) {
      console.error('Error loading booking link:', err);
      if (err.response?.status === 404) {
        setError('This booking link was not found.');
      } else if (err.response?.status === 410) {
        setError('This booking link has expired or reached its usage limit.');
      } else {
        setError('Failed to load booking link. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [linkId]);

  useEffect(() => {
    loadBookingLink();
  }, [loadBookingLink]);

  // Fetch active banners (similar to LeadForm implementation)
  useEffect(() => {
    const fetchBanners = async () => {
      try {
        // Extract postcodes from form data if available, otherwise use empty params
        const params = new URLSearchParams();
        
        console.log('BookingLinkForm: Fetching banners...');
        const { data } = await axios.get(`/api/admin/public/banners?${params.toString()}`);
        const bannerArray = Array.isArray(data) ? data : [];
        console.log('BookingLinkForm: Fetched banners:', bannerArray.length, 'banners:', bannerArray);
        setBanners(bannerArray);
      } catch (err) {
        console.warn('BookingLinkForm: Fetch banners failed', err?.message || err);
        // Fail silently - banners are optional
      }
    };

    fetchBanners();
  }, []);

  // Banner rotation effect
  useEffect(() => {
    if (banners.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentBannerIndex(prev => (prev + 1) % banners.length);
    }, (banners[currentBannerIndex]?.rotationInterval || 5) * 1000);

    return () => clearInterval(interval);
  }, [banners, currentBannerIndex]);

  // Record banner impression when banner is shown
  useEffect(() => {
    if (banners.length > 0 && banners[currentBannerIndex]) {
      const recordImpression = async () => {
        try {
          await axios.post(`/api/admin/banners/${banners[currentBannerIndex]._id}/impression`);
        } catch (e) {
          // silently ignore impression tracking errors
          console.warn('Failed to record banner impression:', e?.message || e);
        }
      };
      recordImpression();
    }
  }, [currentBannerIndex, banners]);

  const handleBannerClick = async (bannerId) => {
    try {
      await axios.post(`/api/admin/banners/${bannerId}/click`);
    } catch (e) {
      // silently ignore click tracking errors
      console.warn('Failed to record banner click:', e?.message || e);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Check passenger capacity when passengers field changes
    if (name === 'passengers' && bookingLink?.driverCarSeats) {
      const selectedPassengers = parseInt(value, 10);
      const driverCapacity = parseInt(bookingLink.driverCarSeats, 10);
      
      if (!isNaN(driverCapacity) && selectedPassengers > driverCapacity) {
        setShowPassengerWarning(true);
        setPassengerCapacity(driverCapacity);
        
        // Show modal instead of flash
        setShowPassengerModal(true);
      } else {
        setShowPassengerWarning(false);
        setPassengerCapacity(null);
      }
    }
    
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  

  // Validation functions
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    if (!phone || typeof phone !== 'string') return false;
    const trimmed = phone.trim();
    if (!trimmed) return false;

    const normalised = trimmed.replace(/[\s()-]/g, '');
    const hasPlusPrefix = normalised.startsWith('+');
    const numericPortion = hasPlusPrefix ? normalised.slice(1) : normalised;

    if (!/^\d+$/.test(numericPortion)) {
      return false;
    }

    const length = numericPortion.length;
    return length >= 7 && length <= 15;
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    // Name validation
    if (!formData.name || formData.name.trim() === '') {
      errors.name = 'Full name is required';
      isValid = false;
    } else if (formData.name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
      isValid = false;
    }

    // Email validation
    if (!formData.email || formData.email.trim() === '') {
      errors.email = 'Email address is required';
      isValid = false;
    } else if (!validateEmail(formData.email)) {
      errors.email = 'Please enter a valid email address';
      isValid = false;
    }

    // Phone validation
    if (!formData.phone || formData.phone.trim() === '') {
      errors.phone = 'Phone number is required';
      isValid = false;
    } else if (!validatePhone(formData.phone)) {
      errors.phone = 'Please enter a valid phone number (add country code if needed)';
      isValid = false;
    }

    // From address validation
    if (!formData.from || formData.from.trim() === '') {
      errors.from = 'Pickup address is required';
      isValid = false;
    } else if (formData.from.trim().length < 5) {
      errors.from = 'Please enter a complete pickup address';
      isValid = false;
    }

    // To address validation
    if (!formData.to || formData.to.trim() === '') {
      errors.to = 'Destination address is required';
      isValid = false;
    } else if (formData.to.trim().length < 5) {
      errors.to = 'Please enter a complete destination address';
      isValid = false;
    }

    // Date/Time validation
    if (!formData.dateTime) {
      errors.dateTime = 'Date and time is required';
      isValid = false;
    } else if (!formData.isASAP && formData.dateTime <= new Date()) {
      errors.dateTime = 'Please select a future date and time';
      isValid = false;
    }

    // Passengers validation
    if (!formData.passengers || parseInt(formData.passengers) < 1) {
      errors.passengers = 'Number of passengers is required';
      isValid = false;
    }

    setFieldErrors(errors);
    return isValid;
  };

  // DateTimePicker change handler - handles ASAP toggle
  const handleDateChange = (date, isASAP = false) => {
    try {
      if (isASAP) {
        // Add 5 minutes to current time when ASAP is toggled
        const now = new Date();
        now.setMinutes(now.getMinutes() + 5);
        setFormData(fd => ({ ...fd, dateTime: now, isASAP }));
      } else {
        setFormData(fd => ({ ...fd, dateTime: date, isASAP }));
      }
    } catch (error) {
      console.warn('Error in handleDateChange:', error);
      // Fallback to current date if there's an error
      setFormData(fd => ({ ...fd, dateTime: new Date(), isASAP }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});
    setSubmitting(true);

    try {
      // Validate form before submission
      if (!validateForm()) {
        setSubmitting(false);
        return;
      }

      // Clean addresses before submission
      const cleanedFormData = {
        ...formData,
        from: cleanGoogleMapsAddress(formData.from),
        to: cleanGoogleMapsAddress(formData.to),
        passengers: parseInt(formData.passengers, 10),
        dateTime: formData.dateTime.toISOString(),
        deviceFingerprint: generateDeviceFingerprint(),
        platform: getDevicePlatform()
      };

      const { data } = await axios.post(`/booking-links/public/${linkId}/submit`, cleanedFormData);

      // Redirect to success page
      navigate('/booking-success', { 
        state: { 
          leadId: data.leadId,
          bookingId: data.bookingId, // Friendly timestamp-based ID
          driverName: data.driverName,
          driverFullName: data.driverFullName,
          driverPhone: data.driverPhone,
          companyName: data.companyName,
          companyLogo: data.companyLogo,
          bookingLinkName: bookingLink?.name,
          whatToExpect: bookingLink?.prefillData?.whatToExpect
        }
      });
    } catch (err) {
      console.error('Error submitting booking:', err);
      
      if (err.response?.status === 404) {
        setError('This booking link was not found. Please check the link and try again.');
      } else if (err.response?.status === 410) {
        setError('This booking link has expired or reached its usage limit. Please contact the driver directly.');
      } else if (err.response?.status === 400) {
        // Handle validation errors from backend
        if (err.response.data.error.includes('Missing required fields')) {
          setError(`Please fill in all required fields: ${err.response.data.error.split(': ')[1]}`);
        } else if (err.response.data.error.includes('valid email')) {
          setError('Please enter a valid email address.');
        } else if (err.response.data.error.toLowerCase().includes('journey today')) {
          setError('You already have a journey today with that pickup or destination. Please contact the driver directly to make any changes.');
        } else if (err.response.data.error.toLowerCase().includes('valid phone')) {
          setError('Please enter a valid phone number (add country code if needed).');
        } else if (err.response.data.error.includes('future date')) {
          setError('Please select a future date and time.');
        } else if (err.response.data.error.includes('Validation error')) {
          setError(`Validation error: ${err.response.data.error}`);
        } else {
          setError(`Validation error: ${err.response.data.error}`);
        }
      } else if (err.response?.status === 409) {
        // Handle duplicate booking error
        setError('A booking with these exact details has already been submitted. Please check your details or contact the driver directly.');
      } else if (err.response?.data?.error) {
        setError(`Submission failed: ${err.response.data.error}`);
      } else if (err.code === 'NETWORK_ERROR' || !err.response) {
        setError('Network error: Please check your internet connection and try again.');
      } else {
        setError('Failed to submit booking. Please try again or contact support if the problem persists.');
      }
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Container className="mt-5">
        <div className="text-center">
          <Spinner animation="border" />
          <p className="mt-3">Loading booking form...</p>
        </div>
      </Container>
    );
  }

  if (error && !bookingLink) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <h4>Booking Link Error</h4>
          <p>{error}</p>
          <Button variant="primary" onClick={() => navigate('/')}>
            Return to Home
          </Button>
        </Alert>
      </Container>
    );
  }

  if (loadError) {
    return (
      <Container className="mt-4">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card>
              <Card.Header className="text-center">
                <div className="mb-3">
                  {bookingLink?.driver?.logo && (
                    <img 
                      src={bookingLink.driver.logo} 
                      alt={`${bookingLink.driver.companyName || 'Company'} logo`}
                      style={{ maxHeight: '60px', maxWidth: '150px' }}
                      className="img-fluid mb-2"
                    />
                  )}
                  <h3>
                    Book with {bookingLink?.driver?.companyName || 'Company'} - {bookingLink?.driver?.name || 'Your Driver'}
                  </h3>
                </div>
                {bookingLink?.name && (
                  <p className="text-muted mb-0">{bookingLink.name}</p>
                )}
              </Card.Header>
              <Card.Body>
                {/* Banner Area */}
                <BannerDisplay banners={banners} currentIndex={currentBannerIndex} />

                <div className="text-center mb-4">
                  <h2 className="text-primary mb-3">
                    <strong>Taxi-Local.uk</strong>
                  </h2>
                  <div className="alert alert-warning">
                    <h5 className="alert-heading">⚠️ Google Maps Loading Issue</h5>
                    <p>We're experiencing issues loading Google Maps. You can still submit your booking request using manual address entry.</p>
                    <Button variant="primary" onClick={() => window.location.reload()}>
                      Try Again
                    </Button>
                  </div>
                </div>

                <MobileBookingForm 
                  formData={formData}
                  handleChange={handleChange}
                  handleDateChange={handleDateChange}
                  fieldErrors={fieldErrors}
                  bookingLink={bookingLink}
                  handleSubmit={handleSubmit}
                  submitting={submitting}
                  setError={setError}
                  showPassengerWarning={showPassengerWarning}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Mobile timeout fallback
  if (mapLoadTimeout && !isLoaded) {
    return (
      <Container className="mt-4">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card>
              <Card.Header className="text-center">
                <div className="mb-3">
                  {bookingLink?.driver?.logo && (
                    <img 
                      src={bookingLink.driver.logo} 
                      alt={`${bookingLink.driver.companyName || 'Company'} logo`}
                      style={{ maxHeight: '60px', maxWidth: '150px' }}
                      className="img-fluid mb-2"
                    />
                  )}
                  <h3>
                    Book with {bookingLink?.driver?.companyName || 'Company'} - {bookingLink?.driver?.name || 'Your Driver'}
                  </h3>
                </div>
                {bookingLink?.name && (
                  <p className="text-muted mb-0">{bookingLink.name}</p>
                )}
              </Card.Header>
              <Card.Body>
                {/* Banner Area */}
                <BannerDisplay banners={banners} currentIndex={currentBannerIndex} />

                <div className="text-center mb-4">
                  <h2 className="text-primary mb-3">
                    <strong>Taxi-Local.uk</strong>
                  </h2>
                  <div className="alert alert-info">
                    <h5 className="alert-heading">📱 Mobile Mode</h5>
                    <p>Google Maps is taking longer to load on your device. You can still submit your booking request using manual address entry.</p>
                  </div>
                </div>

                <MobileBookingForm 
                  formData={formData}
                  handleChange={handleChange}
                  handleDateChange={handleDateChange}
                  fieldErrors={fieldErrors}
                  bookingLink={bookingLink}
                  handleSubmit={handleSubmit}
                  submitting={submitting}
                  setError={setError}
                  showPassengerWarning={showPassengerWarning}
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // Show loading state for Google Maps if not loaded yet and not timed out
  if (!isLoaded && !mapLoadTimeout) {
    return (
      <Container className="mt-4">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card>
              <Card.Header className="text-center">
                <div className="mb-3">
                  {bookingLink?.driver?.logo && (
                    <img 
                      src={bookingLink.driver.logo} 
                      alt={`${bookingLink.driver.companyName || 'Company'} logo`}
                      style={{ maxHeight: '60px', maxWidth: '150px' }}
                      className="img-fluid mb-2"
                    />
                  )}
                  <h3>
                    Book with {bookingLink?.driver?.companyName || 'Company'} - {bookingLink?.driver?.name || 'Your Driver'}
                  </h3>
                </div>
                {bookingLink?.name && (
                  <p className="text-muted mb-0">{bookingLink.name}</p>
                )}
              </Card.Header>
              <Card.Body>
                <div className="text-center mb-4">
                  <h2 className="text-primary mb-3">
                    <strong>Taxi-Local.uk</strong>
                  </h2>
                  <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3">Loading booking form...</p>
                    {isMobile && (
                      <p className="text-muted">This may take a moment on mobile devices</p>
                    )}
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card>
                         <Card.Header className="text-center">
               <div className="mb-3">
                 {bookingLink?.driver?.logo && (
                   <img 
                     src={bookingLink.driver.logo} 
                     alt={`${bookingLink.driver.companyName || 'Company'} logo`}
                     style={{ maxHeight: '60px', maxWidth: '150px' }}
                     className="img-fluid mb-2"
                   />
                 )}
                 <h3>
                   Book with {bookingLink?.driver?.companyName || 'Company'} - {bookingLink?.driver?.name?.split(' ')[0] || 'Your Driver'}
                 </h3>
               </div>
               {bookingLink?.name && (
                 <p className="text-muted mb-0">{bookingLink.name}</p>
               )}
             </Card.Header>
            <Card.Body>
              {/* Banner Area */}
              <BannerDisplay banners={banners} currentIndex={currentBannerIndex} onBannerClick={handleBannerClick} />

              {/* Taxi-Local.uk Heading with Instructions */}
              <div className="text-center mb-4">
                <h2 className="text-primary mb-3">
                  <strong>Taxi-Local.uk</strong>
                </h2>
                <div className="alert alert-info">
              <h5 className="alert-heading">📋 What to Expect</h5>
              <div className="text-start">
                {bookingLink?.prefillData?.whatToExpect ? (
                  <div dangerouslySetInnerHTML={{ __html: bookingLink.prefillData.whatToExpect.replace(/\n/g, '<br>') }} />
                ) : (
                  <>
                    <p className="mb-2"><strong>1. Complete Your Details:</strong> Fill in all required information below to request your taxi service.</p>
                    <p className="mb-2"><strong>2. Driver Review:</strong> Your request will be sent directly to your chosen driver for review.</p>
                    <p className="mb-2"><strong>3. Driver Contact:</strong> The driver depending if currently available,(eg not Driving), will review request. If Request fits in with current Schedule will immediately "Accept" and a Confirmation email will be sent, OR! the Driver may contact you to discuss options.</p>
                    <p className="mb-0"><strong>4. Confirmation:</strong> Once accepted, you'll receive a confirmation email with all booking details.</p>
                  </>
                )}
              </div>
            </div>
          </div>

              {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                  <strong>⚠️ Submission Error:</strong> {error}
                </Alert>
              )}

              {/* Helpful submission guidance */}
              <Alert variant="info" className="mb-3">
                <strong>📝 Quick Tips:</strong>
                <ul className="mb-0 mt-2">
                  <li>All fields marked with * are required</li>
                  <li>Include your full phone number (add country code if outside the UK)</li>
                  <li>Enter complete addresses for pickup and destination</li>
                  <li>Select a future date/time unless using ASAP</li>
                </ul>
              </Alert>


              <Form onSubmit={handleSubmit}>
                {/* Date & Time - Using DateTimePicker with ASAP toggle */}
                <Form.Group className="mb-3">
                  <DateTimePicker
                    value={formData.dateTime}
                    onChange={handleDateChange}
                    label="Date & Time"
                    className="mb-3"
                    use24Hour={false}
                    show24HourToggle={false}
                  />
                  {fieldErrors.dateTime && (
                    <div className="invalid-feedback d-block">
                      {fieldErrors.dateTime}
                    </div>
                  )}
                </Form.Group>

                {/* Name */}
                <Form.Group className="mb-3">
                  <Form.Label>Full Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter your full name"
                    isInvalid={!!fieldErrors.name}
                  />
                  {fieldErrors.name && (
                    <Form.Control.Feedback type="invalid">
                      {fieldErrors.name}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                {/* Email */}
                <Form.Group className="mb-3">
                  <Form.Label>Email Address *</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Enter your email address"
                    isInvalid={!!fieldErrors.email}
                  />
                  {fieldErrors.email && (
                    <Form.Control.Feedback type="invalid">
                      {fieldErrors.email}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                {/* Phone */}
                <Form.Group className="mb-3">
                  <Form.Label>Phone Number *</Form.Label>
                  <SimplePhoneInput
                    value={formData.phone}
                    onChange={(value) => {
                      setFormData(prev => ({ ...prev, phone: value }));
                      // Clear field error when user starts typing
                      if (fieldErrors.phone) {
                        setFieldErrors(prev => ({ ...prev, phone: '' }));
                      }
                    }}
                    required
                    className={fieldErrors.phone ? 'is-invalid' : ''}
                  />
                  {fieldErrors.phone && (
                    <div className="invalid-feedback">
                      {fieldErrors.phone}
                    </div>
                  )}
                </Form.Group>

                {/* From */}
                <Form.Group className="mb-3">
                  <Form.Label>From Address *</Form.Label>
                  {isLoaded ? (
                    <input
                      ref={fromInputRef}
                      id="from"
                      className={`form-control ${fieldErrors.from ? 'is-invalid' : ''}`}
                      placeholder="Pickup address"
                      value={formData.from}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, from: e.target.value }));
                        // Clear field error when user starts typing
                        if (fieldErrors.from) {
                          setFieldErrors(prev => ({ ...prev, from: '' }));
                        }
                      }}
                      required
                    />
                  ) : (
                    <Form.Control
                      type="text"
                      name="from"
                      value={formData.from}
                      onChange={handleChange}
                      required
                      placeholder="Pickup address"
                      isInvalid={!!fieldErrors.from}
                    />
                  )}
                  {fieldErrors.from && (
                    <div className="invalid-feedback">
                      {fieldErrors.from}
                    </div>
                  )}
                </Form.Group>

                {/* To */}
                <Form.Group className="mb-3">
                  <Form.Label>To Address *</Form.Label>
                  {isLoaded ? (
                    <input
                      ref={toInputRef}
                      id="to"
                      className={`form-control ${fieldErrors.to ? 'is-invalid' : ''}`}
                      placeholder="Destination address"
                      value={formData.to}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, to: e.target.value }));
                        // Clear field error when user starts typing
                        if (fieldErrors.to) {
                          setFieldErrors(prev => ({ ...prev, to: '' }));
                        }
                      }}
                      required
                    />
                  ) : (
                    <Form.Control
                      type="text"
                      name="to"
                      value={formData.to}
                      onChange={handleChange}
                      required
                      placeholder="Destination address"
                      isInvalid={!!fieldErrors.to}
                    />
                  )}
                  {fieldErrors.to && (
                    <div className="invalid-feedback">
                      {fieldErrors.to}
                    </div>
                  )}
                </Form.Group>

                {/* Passengers */}
                <Form.Group className="mb-3">
                  <Form.Label>Number of Passengers *</Form.Label>
                  <Form.Select
                    name="passengers"
                    value={formData.passengers}
                    onChange={handleChange}
                    required
                    isInvalid={!!fieldErrors.passengers}
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                      <option key={num} value={num}>
                        {num} {num === 1 ? 'passenger' : 'passengers'}
                      </option>
                    ))}
                  </Form.Select>
                  {fieldErrors.passengers && (
                    <Form.Control.Feedback type="invalid">
                      {fieldErrors.passengers}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>

                {/* Return Trip */}
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="returnTrip"
                    checked={formData.returnTrip}
                    onChange={handleChange}
                    label="Return trip required"
                  />
                </Form.Group>

                {/* Special Requirements */}
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        name="dog"
                        checked={formData.dog}
                        onChange={handleChange}
                        label="Dog/Pet"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        name="disabled"
                        checked={formData.disabled}
                        onChange={handleChange}
                        label="Disabled access required"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                {/* Notes */}
                <Form.Group className="mb-4">
                  <Form.Label>Special Instructions</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Any special requirements or instructions..."
                  />
                </Form.Group>

                {/* Submit Button */}
                <div className="d-grid">
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Submitting...
                      </>
                    ) : (
                      'Submit Booking Request'
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Passenger Capacity Warning Modal */}
      <Modal 
        show={showPassengerModal} 
        onHide={() => setShowPassengerModal(false)}
        centered
        backdrop="static"
      >
        <Modal.Header style={{ background: 'linear-gradient(135deg, #ffc107, #ff9800)', border: 'none' }}>
          <Modal.Title className="text-white">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            Passenger Capacity Notice
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-3">
            <h5 className="text-warning">⚠️</h5>
          </div>
          <p className="mb-3" style={{ fontSize: '16px' }}>
            <strong>This driver is only allowed to carry {passengerCapacity} passenger{passengerCapacity > 1 ? 's' : ''}.</strong>
          </p>
          <p className="mb-3" style={{ fontSize: '14px', color: '#666' }}>
            If you require more passengers, the driver will attempt to get other local drivers involved with your request or may do multiple trips.
          </p>
          <p className="mb-0" style={{ fontSize: '14px', color: '#666' }}>
            <strong>Your driver will contact you shortly to discuss.</strong>
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowPassengerModal(false)} size="lg" className="w-100">
            OK, I Understand
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
